import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GraduationCap, Download, Trash2, Upload, Award, ExternalLink,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  myTraining, uploadExternalCert, deleteExternalCert, openExternalCert,
  academyOpenAuthenticated,
} from '../../../services/api';

const BADGE = {
  valid: 'bg-emerald-100 text-emerald-800',
  due_soon: 'bg-amber-100 text-amber-800',
  overdue: 'bg-rose-100 text-rose-800',
};

export default function MyTrainingHub() {
  const [data, setData] = useState({ academy: [], external: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await myTraining()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load training'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const combined = useMemo(() => {
    const rows = [
      ...data.academy.map(c => ({
        ...c,
        _title: c.courseTitle || c.courseId || '—',
        _date: c.completedAt || c.issuedAt,
        _due: c.refresherDueAt,
      })),
      ...data.external.map(c => ({
        ...c,
        _title: c.courseName,
        _date: c.completedAt,
        _due: c.expiryDate,
      })),
    ].sort((a, b) => (b._date || '').localeCompare(a._date || ''));
    const q = filter.trim().toLowerCase();
    return q ? rows.filter(r =>
      (r._title || '').toLowerCase().includes(q) ||
      (r.provider || '').toLowerCase().includes(q) ||
      (r.certNumber || '').toLowerCase().includes(q)
    ) : rows;
  }, [data, filter]);

  const openCert = (c) => {
    if (c.source === 'academy') {
      academyOpenAuthenticated(`/academy/certificates/${encodeURIComponent(c.certNumber)}/pdf`,
        `${c._title}-${c.certNumber}.pdf`);
    } else {
      openExternalCert(c.id, c.fileName);
    }
  };

  const removeExternal = async (c) => {
    if (!window.confirm(`Delete your external certificate for "${c._title}"? This cannot be undone.`)) return;
    try { await deleteExternalCert(c.id); toast.success('Certificate removed'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="my-training-hub">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
            <GraduationCap className="h-3.5 w-3.5" />Lutrom Engineering
          </div>
          <h1 className="text-2xl font-display font-bold">My Training</h1>
          <p className="text-sm text-slate-500 mt-1">Lutrom Academy certificates plus external qualifications you've uploaded.</p>
        </div>
        <Button onClick={() => setShowUpload(true)} data-testid="my-training-add-external">
          <Upload className="h-4 w-4 mr-1.5" />Add external certificate
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by course, provider or cert number…"
          className="flex-1 border rounded px-3 py-1.5 text-sm max-w-md"
          data-testid="my-training-filter"
        />
        <Link to="/employee/hub" className="text-xs text-slate-500 hover:text-slate-700">← Hub</Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="hidden sm:grid sm:grid-cols-[1fr,120px,120px,120px,100px,110px] gap-3 px-4 py-2 bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
          <div>Course / Qualification</div>
          <div>Source</div>
          <div>Completed</div>
          <div>Due / Expires</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>
        {loading && <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>}
        {!loading && combined.length === 0 && (
          <div className="p-10 text-center text-slate-500">
            <Award className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            No certificates yet. Finish an Academy course or upload an external one.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {combined.map((c, i) => (
            <div
              key={`${c.source}-${c.certNumber || c.id || i}`}
              className="sm:grid sm:grid-cols-[1fr,120px,120px,120px,100px,110px] gap-3 px-4 py-3 items-center flex flex-wrap"
              data-testid={`my-training-row-${i}`}
            >
              <div className="min-w-0 flex-1 sm:flex-none">
                <div className="font-medium text-slate-900 truncate">{c._title}</div>
                <div className="text-xs text-slate-500 truncate">
                  {c.source === 'academy'
                    ? `v${c.courseVersion || '—'} · ${c.certNumber || '—'}`
                    : `${c.provider || 'Self-declared'}${c.certNumber ? ' · ' + c.certNumber : ''}`}
                </div>
                {c.notes && c.source === 'external' && <div className="text-xs text-slate-400 italic mt-0.5 truncate">{c.notes}</div>}
              </div>
              <div className="text-xs">
                <span className={`px-2 py-0.5 rounded ${c.source === 'academy' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                  {c.source === 'academy' ? 'Lutrom' : 'External'}
                </span>
              </div>
              <div className="text-xs text-slate-600">{(c._date || '').slice(0, 10) || '—'}</div>
              <div className="text-xs text-slate-600">{(c._due || '').slice(0, 10) || '—'}</div>
              <div>
                <span className={`text-xs px-2 py-0.5 rounded ${BADGE[c.status] || 'bg-slate-100 text-slate-700'}`}>
                  {c.status === 'valid' ? 'Valid' : c.status === 'due_soon' ? 'Due soon' : c.status === 'overdue' ? 'Overdue' : '—'}
                </span>
              </div>
              <div className="flex items-center gap-1 sm:justify-end mt-2 sm:mt-0">
                <button onClick={() => openCert(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                  title="Open / download" data-testid={`my-training-open-${i}`}>
                  <Download className="h-4 w-4" />
                </button>
                {c.source === 'external' && (
                  <button onClick={() => removeExternal(c)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                    title="Delete" data-testid={`my-training-delete-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {c.source === 'academy' && c.courseId && (
                  <Link to={`/employee/academy/course/${c.courseId}/${c.courseVersion || c.version}`}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Open course">
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showUpload && (
        <UploadDialog onClose={() => setShowUpload(false)} onSaved={async () => { setShowUpload(false); await load(); }} />
      )}
    </div>
  );
}

function UploadDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ courseName: '', provider: '', completedAt: '', expiryDate: '', certNumber: '', notes: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.courseName.trim()) { toast.error('Course name is required'); return; }
    if (!form.completedAt) { toast.error('Completion date is required'); return; }
    if (!file) { toast.error('Please choose a certificate file'); return; }
    setBusy(true);
    try {
      await uploadExternalCert(form, file);
      toast.success('External certificate uploaded');
      await onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload failed');
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white w-full sm:max-w-lg sm:rounded-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
        data-testid="external-cert-upload-form">
        <h2 className="font-display text-lg font-bold mb-3">Add external certificate</h2>
        <div className="space-y-3">
          <Field label="Course / Qualification *" required>
            <input className="w-full border rounded px-2 py-1.5 text-sm" value={form.courseName}
              onChange={update('courseName')} data-testid="ext-cert-course-name" />
          </Field>
          <Field label="Training provider / awarding body">
            <input className="w-full border rounded px-2 py-1.5 text-sm" value={form.provider} onChange={update('provider')} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Completion date *" required>
              <input type="date" className="w-full border rounded px-2 py-1.5 text-sm" value={form.completedAt} onChange={update('completedAt')} />
            </Field>
            <Field label="Expiry date (if any)">
              <input type="date" className="w-full border rounded px-2 py-1.5 text-sm" value={form.expiryDate} onChange={update('expiryDate')} />
            </Field>
          </div>
          <Field label="Certificate number (if any)">
            <input className="w-full border rounded px-2 py-1.5 text-sm" value={form.certNumber} onChange={update('certNumber')} />
          </Field>
          <Field label="Notes (optional)">
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={update('notes')} />
          </Field>
          <Field label="Certificate file (PDF or image) *" required>
            <input type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm" data-testid="ext-cert-file-input" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <Button type="submit" disabled={busy} data-testid="ext-cert-submit">
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
